import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { archiveDeal, archiveOpp, markLeadContacted, patchQualification } from './crmApi'
import { ConfirmDialog, crmToast } from './crmUi'
import { useAuth } from '../../contexts/AuthContext'

/**
 * LEAD KEBAB (Lee, 17 Sep 2026) — the ⋮ quick-actions menu on lead rows
 * (show screen + Leads page), modelled on the Move/Archive menu Lee showed.
 * Everything routes through the EXISTING endpoints — same cascades as doing
 * it the long way (mark-contacted stamps first_contact_at, nurture/disqualify
 * hit the qualification engine, archive is the same soft-delete).
 */

const Item: React.FC<{ icon: React.ReactNode; label: string; danger?: boolean; onClick: () => void }> = ({ icon, label, danger, onClick }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick() }}
    className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-[12px] font-medium text-left hover:bg-surface-hover ${danger ? 'text-red-600' : 'text-txt-primary'}`}
  >
    <span className="w-4 h-4 inline-flex items-center justify-center shrink-0">{icon}</span>
    {label}
  </button>
)
const I: React.FC<{ d: string }> = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><path d={d} /></svg>
)

export const LeadKebab: React.FC<{
  dealId?: string | null
  dealName: string
  email?: string | null
  phone?: string | null
  /** When set, offers "Remove from this show" (archives the capture row only). */
  oppId?: string
  onChanged?: () => void
}> = ({ dealId, dealName, email, phone, oppId, onChanged }) => {
  const navigate = useNavigate()
  const { currentAgent } = useAuth()
  const isAdmin = currentAgent?.role === 'admin'
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; label: string; run: () => Promise<void> }>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc) }
  }, [open])

  const done = (msg: string) => { crmToast(msg, 'success'); setOpen(false); onChanged?.() }
  const fail = (msg: string) => crmToast(msg, 'error')

  return (
    <div ref={ref} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover"
        title="Quick actions"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-40 w-56 rounded-xl border border-border bg-surface shadow-lg py-1.5">
          {dealId && <Item icon={<I d="M7 17L17 7M7 7h10v10" />} label="Open lead" onClick={() => navigate(`/crm/lead/${dealId}`)} />}
          {phone && <Item icon={<I d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.58 2.81.7A2 2 0 0 1 22 16.92z" />} label={`Call ${phone}`} onClick={() => { window.location.href = `tel:${phone}` }} />}
          {email && <Item icon={<I d="M4 4h16v16H4zM4 6l8 7 8-7" />} label="Email lead" onClick={() => { window.location.href = `mailto:${email}` }} />}
          {dealId && (
            <Item icon={<I d="M20 6 9 17l-5-5" />} label="Mark contacted" onClick={() =>
              markLeadContacted(dealId, 'Marked contacted (quick action)').then(() => done('Marked contacted')).catch(() => fail('Could not mark contacted'))
            } />
          )}
          {dealId && (
            <Item icon={<I d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2" />} label="Move to Nurture" onClick={() =>
              patchQualification(dealId, { action: 'nurture', reason: 'Quick action (kebab menu)' }).then(() => done('Moved to Nurture')).catch(() => fail('Could not move to Nurture'))
            } />
          )}
          {dealId && (
            <Item danger icon={<I d="M18 6 6 18M6 6l12 12" />} label="Disqualify" onClick={() => setConfirm({
              title: `Disqualify ${dealName}?`,
              message: 'Removes them from every working list (reversible from the lead).',
              label: 'Disqualify',
              run: async () => { await patchQualification(dealId, { action: 'disqualify', reason: 'Quick action (kebab menu)' }); done('Disqualified') },
            })} />
          )}
          {oppId && (
            <Item danger icon={<I d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />} label="Remove from this show" onClick={() => setConfirm({
              title: `Remove ${dealName} from this show?`,
              message: 'Only the capture entry goes — the CRM lead (if any) is untouched.',
              label: 'Remove',
              run: async () => { await archiveOpp(oppId); done('Removed from the show') },
            })} />
          )}
          {dealId && isAdmin && (
            <Item danger icon={<I d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />} label="Archive lead" onClick={() => setConfirm({
              title: `Archive ${dealName}?`,
              message: 'Hidden from every list, board and metric — restorable from the Archived tab.',
              label: 'Archive',
              run: async () => { await archiveDeal(dealId); done('Lead archived') },
            })} />
          )}
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.label}
          onConfirm={async () => { try { await confirm.run() } catch { fail('Action failed') } setConfirm(null) }}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  )
}
